import React, { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import Modal from "../design-system/Modal";
import { Box } from "../design-system";
import { userContext } from "../../stores/users/userContext";
import { EDIT_PROFILE_MODAL } from "./constants/accountText";
import type { EditProfileFormData } from "../../types/user";
import EditProfileHeader from "./components/EditProfileHeader";
import EditProfileForm from "./components/EditProfileForm";
import { Button } from "../design-system";

export interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  currentFullName: string;
}

const EditProfileModal = ({
  open,
  onClose,
  currentFullName,
}: EditProfileModalProps) => {
  const userStore = useContext(userContext);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid },
  } = useForm<EditProfileFormData>({
    mode: "onChange",
    defaultValues: { full_name: currentFullName },
  });

  useEffect(() => {
    if (open) {
      reset({ full_name: currentFullName });
    }
  }, [open, currentFullName, reset]);

  const handleCancel = () => {
    reset({ full_name: currentFullName });
    onClose();
  };

  const onSubmit = async (data: EditProfileFormData) => {
    const formData = new FormData();
    formData.append("full_name", data.full_name.trim());
    await userStore.updateUserProfile(formData);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      PaperProps={{
        sx: {
          width: "525px",
          maxWidth: "525px",
          height: "auto",
          maxHeight: "none",
          "& > div": {
            minHeight: "unset",
            height: "auto",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "24px 32px",
          gap: "24px",
        }}
      >
        <EditProfileHeader onClose={handleCancel} />

        <EditProfileForm
          register={register}
          onSubmit={handleSubmit(onSubmit)}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{ borderRadius: "100px" }}
          >
            {EDIT_PROFILE_MODAL.CANCEL}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {EDIT_PROFILE_MODAL.SAVE}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditProfileModal;
